// Форматирование дат/времени в часовом поясе клуба (ru-RU).

// Короткие/нестандартные названия из настроек клуба → IANA-зоны.
const TZ_ALIASES: Record<string, string> = {
  minsk: 'Europe/Minsk',
  moscow: 'Europe/Moscow',
  msk: 'Europe/Moscow',
  kyiv: 'Europe/Kyiv',
  kiev: 'Europe/Kyiv'
}

// Кэш результатов нормализации (ключ — исходная строка из настроек).
const tzCache = new Map<string, string | undefined>()

/**
 * Приводит часовой пояс клуба к валидному IANA-имени. Настройки могут хранить
 * короткое название («Minsk»), которое Intl.DateTimeFormat не принимает —
 * оно бросает RangeError и роняло весь рендер (белый экран). Невалидную зону
 * заменяем на undefined (локальное время браузера), чтобы UI никогда не падал.
 */
export function normalizeTimeZone(timeZone?: string | null): string | undefined {
  if (!timeZone) return undefined
  if (tzCache.has(timeZone)) return tzCache.get(timeZone)
  const candidate = TZ_ALIASES[timeZone.trim().toLowerCase()] ?? timeZone.trim()
  let resolved: string | undefined
  try {
    new Intl.DateTimeFormat('en-CA', { timeZone: candidate })
    resolved = candidate
  } catch {
    resolved = undefined
  }
  tzCache.set(timeZone, resolved)
  return resolved
}

/** 'YYYY-MM-DD' для даты в указанном часовом поясе (en-CA даёт ISO-формат). */
export function dateKey(d: Date, timeZone?: string | null): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: normalizeTimeZone(timeZone),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(d)
}

/** Время слота 'ЧЧ:ММ' в часовом поясе клуба. */
export function timeLabel(ms: number, timeZone?: string | null): string {
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: normalizeTimeZone(timeZone),
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(ms))
}

/** Подпись дня для кнопок выбора даты: «сегодня», «завтра» или «пн, 16 июн». */
export function dayLabel(
  d: Date,
  timeZone: string | null,
  todayKey: string,
  tomorrowKey: string
): string {
  const key = dateKey(d, timeZone)
  if (key === todayKey) return 'Сегодня'
  if (key === tomorrowKey) return 'Завтра'
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: normalizeTimeZone(timeZone),
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  }).format(d)
}

/** Полная дата+время брони для экрана подтверждения. */
export function fullDateTime(ms: number, timeZone?: string | null): string {
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: normalizeTimeZone(timeZone),
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(ms))
}

/** Денежный формат BYN без лишних нулей. */
export function money(n: number): string {
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(n)
}

/** Список из N дат начиная с сегодняшней (в часовом поясе клуба). */
export function nextDays(count: number): Date[] {
  const out: Date[] = []
  const now = Date.now()
  for (let i = 0; i < count; i++) {
    out.push(new Date(now + i * 24 * 60 * 60 * 1000))
  }
  return out
}
